/**
 * 재시도 관리 유틸리티
 * 지수 백오프 및 재시도 로직 관리
 */

export interface RetryOptions {
    maxAttempts: number;
    baseDelay: number; // 밀리초
    maxDelay: number; // 밀리초
    backoffMultiplier: number;
    jitter: boolean; // 지터 추가 여부
}

export interface RetryResult<T> {
    success: boolean;
    result?: T;
    error?: any;
    attempts: number;
    totalTime: number;
}

export class RetryManager {
    private static readonly DEFAULT_OPTIONS: RetryOptions = {
        maxAttempts: 3,
        baseDelay: 1000, // 1초
        maxDelay: 10000, // 10초
        backoffMultiplier: 2,
        jitter: true
    };

    /**
     * 지터 추가 (네트워크 부하 분산)
     */
    private static addJitter(delay: number): number {
        return delay + Math.random() * delay * 0.1; // 10% 지터
    }

    /**
     * 지연 시간 계산 (지수 백오프)
     */
    private static calculateDelay(attempt: number, options: RetryOptions): number {
        const delay = Math.min(
            options.baseDelay * Math.pow(options.backoffMultiplier, attempt - 1),
            options.maxDelay
        );

        return options.jitter ? this.addJitter(delay) : delay;
    }

    /**
     * 재시도 가능한 에러인지 확인
     */
    static isRetryableError(error: any): boolean {
        // 네트워크 오류
        if (!error.response) {
            return true;
        }

        const status = error.response.status;

        // 재시도 가능한 HTTP 상태 코드
        const retryableStatuses = [
            408, // Request Timeout
            429, // Too Many Requests
            500, // Internal Server Error
            502, // Bad Gateway
            503, // Service Unavailable
            504, // Gateway Timeout
        ];

        return retryableStatuses.includes(status);
    }

    /**
     * 비동기 함수 재시도 실행
     */
    static async retry<T>(
        fn: () => Promise<T>,
        options: Partial<RetryOptions> = {}
    ): Promise<RetryResult<T>> {
        const finalOptions = { ...this.DEFAULT_OPTIONS, ...options };
        const startTime = Date.now();
        let lastError: any;

        for (let attempt = 1; attempt <= finalOptions.maxAttempts; attempt++) {
            try {
                const result = await fn();
                return {
                    success: true,
                    result,
                    attempts: attempt,
                    totalTime: Date.now() - startTime
                };
            } catch (error) {
                lastError = error;

                // 마지막 시도이거나 재시도 불가능한 에러인 경우
                if (attempt === finalOptions.maxAttempts || !this.isRetryableError(error)) {
                    break;
                }

                // 다음 시도 전 대기
                const delay = this.calculateDelay(attempt, finalOptions);
                console.log(`재시도 ${attempt}/${finalOptions.maxAttempts} 실패, ${delay}ms 후 재시도...`, error);

                await this.sleep(delay);
            }
        }

        return {
            success: false,
            error: lastError,
            attempts: finalOptions.maxAttempts,
            totalTime: Date.now() - startTime
        };
    }

    /**
     * 토큰 갱신 전용 재시도 로직
     */
    static async retryTokenRefresh<T>(
        fn: () => Promise<T>,
        options: Partial<RetryOptions> = {}
    ): Promise<RetryResult<T>> {
        const tokenRefreshOptions: RetryOptions = {
            maxAttempts: 2, // 토큰 갱신은 최대 2번만 시도
            baseDelay: 500, // 0.5초
            maxDelay: 2000, // 2초
            backoffMultiplier: 2,
            jitter: false // 토큰 갱신은 지터 없이
        };

        return this.retry(fn, { ...tokenRefreshOptions, ...options });
    }

    /**
     * API 호출 전용 재시도 로직
     */
    static async retryApiCall<T>(
        fn: () => Promise<T>,
        options: Partial<RetryOptions> = {}
    ): Promise<RetryResult<T>> {
        const apiCallOptions: RetryOptions = {
            maxAttempts: 3,
            baseDelay: 1000, // 1초
            maxDelay: 5000, // 5초
            backoffMultiplier: 2,
            jitter: true
        };

        return this.retry(fn, { ...apiCallOptions, ...options });
    }

    /**
     * 지연 함수
     */
    private static sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * 재시도 상태 모니터링
     */
    static createRetryMonitor() {
        const stats = {
            totalAttempts: 0,
            successfulAttempts: 0,
            failedAttempts: 0,
            averageRetryTime: 0,
            retryHistory: [] as Array<{
                timestamp: number;
                success: boolean;
                attempts: number;
                totalTime: number;
                error?: any;
            }>
        };

        return {
            recordAttempt: (result: RetryResult<any>) => {
                stats.totalAttempts++;
                if (result.success) {
                    stats.successfulAttempts++;
                } else {
                    stats.failedAttempts++;
                }

                stats.retryHistory.push({
                    timestamp: Date.now(),
                    success: result.success,
                    attempts: result.attempts,
                    totalTime: result.totalTime,
                    error: result.error
                });

                // 최근 100개 기록만 유지
                if (stats.retryHistory.length > 100) {
                    stats.retryHistory = stats.retryHistory.slice(-100);
                }

                // 평균 재시도 시간 계산
                const totalTime = stats.retryHistory.reduce((sum, record) => sum + record.totalTime, 0);
                stats.averageRetryTime = totalTime / stats.retryHistory.length;
            },

            getStats: () => ({ ...stats }),

            reset: () => {
                stats.totalAttempts = 0;
                stats.successfulAttempts = 0;
                stats.failedAttempts = 0;
                stats.averageRetryTime = 0;
                stats.retryHistory = [];
            }
        };
    }
}

// 전역 재시도 모니터 인스턴스
export const retryMonitor = RetryManager.createRetryMonitor();

export default RetryManager;
